import { CdkDrag, CdkDragDrop, CdkDragMove, CdkDragStart, CdkDropList } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, DOCUMENT, HostListener, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router, RouterEvent, RouterLink } from '@angular/router';
import { NgbHighlight, NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  mergeMap,
  Observable,
  startWith,
  Subject,
  tap,
} from 'rxjs';
import { ProfileService } from 'src/app/auth/services/profile.service';
import { ISpaceCreationContext, SpaceNode } from 'src/app/configurator/spaces/models/space-configuration.models';
import {
  ConfiguratorMutationAction,
  ConfiguratorMutationSyncService,
} from 'src/app/configurator/spaces/services/configurator-mutation-sync.service';
import { DashboardType } from 'src/app/dashboards/models/enums/dashboard-type';
import { IOrganizationResponse, OrganizationHierarchyFlowType } from 'src/app/dashboards/models/spatial.models';
import { ComparisonMode, ComparisonService } from 'src/app/dashboards/services/comparison.service';
import { SpatialService } from 'src/app/dashboards/services/spatial.service';
import { ConfirmationModalComponent } from 'src/app/shared/modals/confirmation-modal/confirmation-modal.component';
import { CreateItemSelectionComponent } from 'src/app/shared/modals/create-item-selection/create-item-selection.component';
import { BaseType, TwinTypes } from 'src/app/shared/models/space-types.enum';
import { SubscriptionAccessSource } from 'src/app/shared/models/subscription-access-source';
import { IBreadcrumb } from 'src/app/shared/services/breadcrumbs/breadcrumb';
import { BreadcrumbsService } from 'src/app/shared/services/breadcrumbs/breadcrumbs.service';
import { HierarchyFlowPreferenceService } from 'src/app/shared/services/hierarchy/hierarchy-flow-preference/hierarchy-flow-preference.service';
import { HierarchyMenuService } from 'src/app/shared/services/hierarchy/hierarchy-menu/hierarchy-menu.service';
import { QueryParametersService } from 'src/app/shared/services/query-parameters/query-parameters.service';
import { SpaceCreationService } from 'src/app/shared/services/space-creation.service';
import { MenuHierarchyService } from '../../../services/menu/menu-hierarchy.service';
import { IMenuItem, MenuItemService } from '../../../services/menu/menu-item.service';
import { MenuSearchService } from '../../../services/menu/menu-search.service';

@Component({
  selector: 'app-hierarchy-menu',
  templateUrl: './hierarchy-menu.component.html',
  styleUrl: './hierarchy-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    NgTemplateOutlet,
    RouterLink,
    NgbTooltip,
    NgbHighlight,
    AsyncPipe,
    CdkDropList,
    CdkDrag,
    CdkScrollable,
  ],
})
export class HierarchyMenuComponent {
  protected canEdit: boolean = false;
  protected canSelect: boolean = false;
  protected canFlow: boolean = false;

  protected menuItems: IMenuItem[] | undefined;
  protected filteredMenuItems: IMenuItem[] | undefined;

  protected selectedItems: string[] = [];
  protected dashboardType: DashboardType = DashboardType.Energy;
  protected initialSelection: string[] = [];
  protected navItems$: Observable<IMenuItem[]> | undefined;

  protected isMultiSelectAlwaysEnabled: boolean = false;
  protected minimumSelections = 2;
  protected maximumSelections = 5;

  protected pageRoute: string;

  protected hasChanged: boolean = false;
  protected isComparisonEnabled: boolean = false;
  protected isEnergyFlowActive: boolean = false;
  protected isMoving: boolean = false;
  protected invalidDropTargetId?: string;
  protected preferredFlowType?: OrganizationHierarchyFlowType;
  protected effectiveFlowType?: OrganizationHierarchyFlowType;

  protected searchInput$ = new BehaviorSubject<string>('');
  protected searchTerm$: Observable<string>;

  protected dropTargetIds: string[] = [];
  protected allowedParentTypes?: Map<string, string[]>;

  protected readonly subscriptionAccessSource = SubscriptionAccessSource;
  protected readonly comparisonService = inject(ComparisonService);

  private selectionType: string | undefined;
  private hasFlowChanged: boolean = true;
  private isMoving$ = new Subject<boolean>();
  private hierarchyFlowType$ = new Subject<OrganizationHierarchyFlowType | undefined>();
  private autoExpandStrategy?: 'firstAsset' | 'none';
  private allowedBaseTypes: BaseType[] | undefined;

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly modalService = inject(NgbModal);
  private readonly document: Document = inject(DOCUMENT);

  private readonly spatialService = inject(SpatialService);
  private readonly breadcrumbsService = inject(BreadcrumbsService);
  private readonly menuItemService = inject(MenuItemService);
  private readonly menuSearchService = inject(MenuSearchService);
  private readonly menuHierarchyService = inject(MenuHierarchyService);
  private readonly spaceCreationService = inject(SpaceCreationService);
  private readonly hierarchyMenuService = inject(HierarchyMenuService);
  private readonly hierarchyFlowPreferenceService = inject(HierarchyFlowPreferenceService);
  private readonly queryParametersService = inject(QueryParametersService);
  private readonly mutationSyncService = inject(ConfiguratorMutationSyncService);
  private readonly profileService = inject(ProfileService);

  private dragCancelled: boolean = false;
  private hover$ = new Subject<Element | null>();
  private grabbedMenuItem?: IMenuItem;
  private dropped: Element | null = null;
  private configLoaded: boolean = false;
  private currentUserId?: string;
  private hasExplicitFlowPreference: boolean = false;

  constructor() {
    this.pageRoute = this.router.url.split('?')[0];

    merge(
      this.mutationSyncService.spaceMutation$.pipe(
        map((m) => ({
          action: m.action,
          id: m.node.id,
          name: m.node.name,
          twinType: m.node.twinType,
          parentId: m.node.parentSpace?.id,
          baseType: this.spaceCreationService.typeToBaseType(m.node.twinType),
        })),
      ),
      this.mutationSyncService.assetMutation$.pipe(
        map((m) => ({
          action: m.action,
          id: m.asset.id,
          name: m.asset.name,
          twinType: TwinTypes.Asset,
          parentId: m.parentId,
          baseType: BaseType.Asset,
        })),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((mutation) => {
        this.applyMutationToHierarchy(mutation);
      });

    this.queryParametersService.queryParameters$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.initialSelection = params.objectIds ?? [];
      this.selectedItems = this.initialSelection;
      if (this.menuItems) {
        this.expandParentsOfSelected(this.menuItems);
      }
      this.updateChangeDetection();
      this.generateBreadCrumbs();

      if (this.configLoaded) {
        this.applyFlowState(params.flow ? OrganizationHierarchyFlowType.Meter : undefined);
      }

      // Ensure comparison mode stays active if multi-select is always enabled
      if (this.isMultiSelectAlwaysEnabled) {
        this.comparisonService.setComparisonMode(ComparisonMode.Assets);
      }
    });

    this.profileService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile) => {
        this.currentUserId = profile.id;

        if (this.configLoaded) {
          this.applyFlowState(
            this.route.snapshot.queryParams['flow'] === 'meter' ? OrganizationHierarchyFlowType.Meter : undefined,
          );
          this.loadHierarchy();
        }
      });

    this.router.events
      .pipe(
        map((event) => (event as RouterEvent).url),
        filter((url) => url != null),
        map((url) => url.split('?')[0]),
        takeUntilDestroyed(this.destroyRef),
        startWith(this.pageRoute),
      )
      .subscribe((baseUrl) => (this.pageRoute = baseUrl));

    this.hierarchyMenuService.config$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((config) => {
      const canEdit = config.hierarchyMenu?.edit ?? false;

      // Clean up drag-and-drop state if edit permissions are removed while moving
      if (this.canEdit && !canEdit && this.isMoving) {
        this.stopMoving();
      }

      this.canEdit = canEdit;
      this.canSelect = config.hierarchyMenu?.select ?? false;
      this.canFlow = config.hierarchyMenu?.flow ?? false;
      this.autoExpandStrategy = config.hierarchyMenu?.autoExpand;
      this.allowedBaseTypes = config.hierarchyMenu?.allowedBaseTypes;
      this.isMultiSelectAlwaysEnabled = config.hierarchyMenu?.isMultiSelectEnabled ?? false;
      this.hasFlowChanged = this.dashboardType !== config.hierarchyMenu?.type;
      this.dashboardType = config.hierarchyMenu?.type ?? this.dashboardType;
      this.applyFlowState(
        this.route.snapshot.queryParams['flow'] === 'meter' ? OrganizationHierarchyFlowType.Meter : undefined,
      );

      // Auto-enable comparison if multi-select is always enabled
      if (this.isMultiSelectAlwaysEnabled) {
        this.comparisonService.setComparisonMode(ComparisonMode.Assets);
        this.minimumSelections = 1;
        this.maximumSelections = Number.POSITIVE_INFINITY;
      }

      if (!this.configLoaded) {
        this.hasFlowChanged = true;
      }
      this.configLoaded = true;

      if (config.hierarchyMenu) {
        this.loadHierarchy();
      }
    });

    // Setup search functionality with debounce and minimum character requirement
    this.searchTerm$ = this.searchInput$.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((searchTerm) => {
        const trimmedTerm = searchTerm.trim().toLowerCase();
        // Only apply search if term has 3+ characters or is empty (to clear search)
        const term = trimmedTerm.length >= 3 ? trimmedTerm : '';
        return term;
      }),
      distinctUntilChanged(),
    );

    // Subscribe to comparison mode changes
    this.comparisonService.comparisonMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mode) => {
      // Update local state based on service state
      this.isComparisonEnabled = mode === ComparisonMode.Assets;
    });

    // Combine isMoving and hierarchyFlowType changes to update allowedParentTypes.
    // Only fetch when isMoving is true — getAllowedParentTypes requires ConfiguratorPermissions.Update
    // and is only needed during drag-and-drop. Filtering on flowType alone would trigger this for
    // all users on component init, causing an unauthorized error for non-editor users.
    combineLatest([this.isMoving$.pipe(startWith(undefined)), this.hierarchyFlowType$.pipe(startWith(undefined))])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([moving]) => !!moving),
        mergeMap(([_, flowType]) => this.spatialService.getAllowedParentTypes(flowType)),
      )
      .subscribe((allowedParentTypes) => {
        this.allowedParentTypes = allowedParentTypes;
        this.updateDropTargetIds();
      });

    // subscribe to search term changes
    this.searchTerm$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((term) => term !== undefined),
      )
      .subscribe((term) => {
        if (this.menuItems) {
          this.filteredMenuItems = this.menuSearchService.filterMenuItems(this.menuItems, this.filteredMenuItems, term);
          this.updateChangeDetection();
        }

        // When search is cleared, expand to show selected items
        if (term.length !== 0 || !this.menuItems) {
          return;
        }

        // First, sync any collapse state changes from filtered items back to main items
        if (this.filteredMenuItems && this.filteredMenuItems !== this.menuItems) {
          this.syncCollapseState(this.filteredMenuItems, this.menuItems);
        }

        // Then expand to show the items that are currently selected (based on selectedItems array)
        this.expandToShowSelectedItems();
      });

    this.hover$
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(1000), distinctUntilChanged())
      .subscribe((element) => this.expandHoveredMenuItem(element));
  }

  @HostListener('window:keyup', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.dragCancelled = true;
      document.dispatchEvent(new Event('mouseup'));
      this.removeSelectedClass();
    }
  }

  protected onSelectionChange(item: IMenuItem): void {
    // Always update the selection in the main menuItems to ensure persistence
    if (this.filteredMenuItems && this.filteredMenuItems !== this.menuItems) {
      // We're in search mode - sync the selection back to main menu items
      this.menuItemService.syncSelectionState(this.filteredMenuItems, this.menuItems!);
    }

    const currentItems = this.filteredMenuItems || this.menuItems;
    this.selectedItems = this.menuItemService.getSelectedItems(currentItems!).map((x) => x.id);

    const selectedAmount = this.selectedItems.length;

    // Apply allowed base types filter if configured (takes precedence over comparison filtering)
    if (this.allowedBaseTypes) {
      this.menuItemService.disableBasedOnType(currentItems, undefined, this.allowedBaseTypes);
    } else if (selectedAmount < this.maximumSelections) {
      // Only apply comparison type filtering if no base type restrictions and not at max
      this.selectionType = item.baseType;
      if (selectedAmount === 0) {
        this.selectionType = undefined;
      }
      this.menuItemService.disableBasedOnType(currentItems, this.selectionType);
    } else {
      // At maximum comparisons - only allow deselecting
      this.menuItemService.disableUnselected(currentItems);
    }
    this.updateChangeDetection();

    // Only expand to show selected items if comparison is not enabled
    if (!this.isComparisonEnabled) {
      this.expandToShowSelectedItems();
    }
  }

  /**
   * Handles toggling of the selection/compare mode.
   * Disabled if multi-select is always enabled.
   */
  protected onSelectChange(): void {
    // Prevent toggling if multi-select is always enabled
    if (this.isMultiSelectAlwaysEnabled) {
      return;
    }

    if (this.isComparisonEnabled) {
      // Check if date comparison is active
      if (this.comparisonService.isComparingDates()) {
        this.isComparisonEnabled = false;
        console.warn('Cannot compare assets while comparing dates');
        return;
      }

      this.comparisonService.setComparisonMode(ComparisonMode.Assets);
    } else {
      this.comparisonService.setComparisonMode(ComparisonMode.None);
      this.queryParametersService.setObjectIds();
    }
    this.updateChangeDetection();
  }

  protected onFlowChange(): void {
    const flowType = this.isEnergyFlowActive
      ? OrganizationHierarchyFlowType.Meter
      : OrganizationHierarchyFlowType.Space;
    this.hasExplicitFlowPreference = true;
    this.preferredFlowType = flowType;
    this.effectiveFlowType = flowType;
    this.isEnergyFlowActive = flowType === OrganizationHierarchyFlowType.Meter;
    if (this.currentUserId) {
      this.hierarchyFlowPreferenceService.setPreferredFlow(flowType, this.currentUserId);
    }
    this.hierarchyFlowType$.next(this.effectiveFlowType);
    this.hasFlowChanged = true;
    this.queryParametersService.setFlow(flowType === OrganizationHierarchyFlowType.Meter);
    this.loadHierarchy();
  }

  protected collapse(item: IMenuItem): void {
    item.collapsed = !item.collapsed;
  }

  /**
   * Determines whether save on
   */
  protected onSave(): void {
    if (this.isComparisonEnabled) {
      this.queryParametersService.setObjectIds(this.selectedItems);
    }

    this.initialSelection = this.selectedItems;
    this.updateChangeDetection();
  }

  protected isActive(queryParams: Params): boolean {
    return this.route.snapshot.queryParams['objecten'] === queryParams['objecten'];
  }

  protected generateQueryParamsForMenuItem(itemQueryParams: Params): Params {
    const currentParams = { ...this.route.snapshot.queryParams };
    currentParams['objecten'] = itemQueryParams['objecten'];
    return currentParams;
  }

  protected grab(event: CdkDragStart<IMenuItem>): void {
    this.grabbedMenuItem = event.source.data;
    this.invalidDropTargetId = undefined;
    this.updateDropTargetIds();
  }

  protected move(event: CdkDragMove<IMenuItem>): void {
    const pointerElement = this.document.elementFromPoint(event.pointerPosition.x, event.pointerPosition.y);

    // Get selectable element
    let element = pointerElement;
    while (element && !element.id) {
      element = element.parentElement;
    }
    this.hover$.next(element);

    const isValidDropTarget = !!element && this.dropTargetIds.includes(element.id);
    this.removeSelectedClass();

    if (isValidDropTarget && element) {
      this.invalidDropTargetId = undefined;
      this.dropped = element;
      element.classList.add('selected');
    } else {
      this.dropped = null;
      this.invalidDropTargetId = element?.id;
      if (element && element.tagName.toLowerCase() === 'li') {
        element.classList.add('not-allowed-target');
      }
      this.document.querySelector('nav + ul')?.classList.add('not-allowed');
    }
  }

  protected drop(event: CdkDragDrop<IMenuItem, IMenuItem, IMenuItem>): void {
    this.invalidDropTargetId = undefined;
    this.grabbedMenuItem = undefined;
    this.removeSelectableClass();
    this.hover$.next(null);

    if (this.dragCancelled) {
      this.dragCancelled = false;
      return;
    }
    this.removeSelectedClass();

    const menuItem = event.item.data;
    const droppedMenuItem = this.dropped ? this.findMenuItemFromElement(this.dropped) : undefined;

    // No valid drop target found
    if (!droppedMenuItem) return;

    // Cannot move item to current location
    if (droppedMenuItem.id === menuItem.parent?.id) return;

    if (this.shouldRestrictToSameBuilding(menuItem) && !this.isSameBuildingMove(menuItem, droppedMenuItem)) {
      return;
    }

    const modalRef = this.modalService.open(ConfirmationModalComponent, { size: 'lg' });
    modalRef.componentInstance.danger = false;
    modalRef.componentInstance.title = `${menuItem.name} verplaatsen`;
    modalRef.componentInstance.message =
      `Weet je zeker dat je "${menuItem.name}" wilt verplaatsen ` +
      `van "${menuItem.parent?.name}" naar "${droppedMenuItem.name}"?`;

    modalRef.closed.subscribe((result) => {
      if (!result) return;

      // Move
      this.spatialService
        .move(menuItem.id, droppedMenuItem.id, this.effectiveFlowType)
        .pipe(
          tap(() => {
            // Remove menuItem from original parent's children
            const originalParent = menuItem.parent;
            if (originalParent && originalParent.children) {
              originalParent.children = originalParent.children.filter((child) => child.id !== menuItem.id);
            }

            // Set new parent
            menuItem.parent = droppedMenuItem;

            // Add menuItem to new parent's children
            if (!droppedMenuItem.children) {
              droppedMenuItem.children = [];
            }

            droppedMenuItem.children.push(menuItem);
            droppedMenuItem.collapsed = false;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    });
  }

  protected dragAndDropClicked(): void {
    this.toggleMoving();
  }

  /**
   * Check if an item should show the add button (only for organizations and sites)
   */
  protected shouldShowAddButton(item: IMenuItem): boolean {
    const isValidType = item.baseType === BaseType.Agent || item.baseType === BaseType.Space;
    const hasPermission = this.canEdit;
    const isNotMoving = !this.isMoving;
    const isNotComparing = !this.isComparisonEnabled;

    return isValidType && hasPermission && isNotMoving && isNotComparing;
  }

  /**
   * Check if the hierarchy only contains the organization node (no children)
   */
  protected hasNoAssets(): boolean {
    if (!this.filteredMenuItems || this.filteredMenuItems.length === 0) {
      return false;
    }

    // Check if we only have the organization node with no children
    const organizationNode = this.filteredMenuItems[0];
    return organizationNode.children.length === 0;
  }

  /**
   * Open the create item modal for the given menu item
   */
  protected async openCreateItemModal(item: IMenuItem): Promise<void> {
    const context: ISpaceCreationContext = {
      id: item.id,
      twinType: item.twinType,
      baseType: item.baseType,
    };

    // Get available creation options using the service
    const options = this.spaceCreationService.getAvailableCreationOptions(context);

    if (options.length === 0) {
      console.warn('No creation options available for this item type');
      return;
    }

    // Open the create item selection modal
    const modalRef = this.modalService.open(CreateItemSelectionComponent, { size: 'lg' });
    modalRef.componentInstance.options = options;

    try {
      const selectedType = (await modalRef.result) as TwinTypes;

      if (selectedType) {
        const createdItem = await this.spaceCreationService.openCreationModal(selectedType, item.id);

        if (selectedType === TwinTypes.Asset) {
          this.mutationSyncService.publishAssetMutation(
            ConfiguratorMutationAction.Created,
            { id: createdItem.id, name: createdItem.name },
            item.id,
          );
          return;
        }

        const spaceNode = createdItem as SpaceNode;
        spaceNode.parentSpace = { id: item.id, name: item.name, twinType: item.twinType as TwinTypes };
        spaceNode.childSpaces = spaceNode.childSpaces ?? [];

        this.mutationSyncService.publishSpaceMutation(ConfiguratorMutationAction.Created, spaceNode);
      }
    } catch (error) {
      console.error('Error opening creation modal:', error);
    }
  }

  /**
   * Safely exits the moving state by ensuring any active/stray DOM classes are reset
   * and any in-flight CDK drag is cancelled
   */
  private stopMoving(): void {
    this.isMoving = false;
    this.isMoving$.next(this.isMoving);

    // In case a drag was actively in progress
    this.dragCancelled = true;
    this.document.dispatchEvent(new Event('mouseup'));

    this.removeSelectedClass();
    this.removeSelectableClass();
  }

  private toggleMoving(): void {
    if (this.isMoving) {
      this.stopMoving();
      return;
    }

    this.isMoving = true;
    this.isMoving$.next(this.isMoving);
  }

  private applyMutationToHierarchy(mutation: {
    action: ConfiguratorMutationAction;
    id: string;
    name: string;
    twinType: TwinTypes;
    parentId?: string;
    baseType: BaseType;
  }): void {
    if (!this.menuItems) return;

    const existingItems = this.menuItemService.getItemsById(this.menuItems, [mutation.id]);

    if (mutation.action === ConfiguratorMutationAction.Deleted) {
      existingItems.forEach((item) => {
        const parent = item.parent;
        if (parent && parent.children) {
          parent.children = parent.children.filter((c) => c.id !== mutation.id);
        }
      });
      // Trigger change detection for angular to re-render the view
      this.menuItems = [...this.menuItems];
      if (this.filteredMenuItems) this.filteredMenuItems = [...this.filteredMenuItems];
      return;
    }

    if (mutation.action === ConfiguratorMutationAction.Updated) {
      existingItems.forEach((item) => (item.name = mutation.name));
      return;
    }

    if (mutation.action === ConfiguratorMutationAction.Created) {
      if (existingItems.length > 0 || !mutation.parentId) return;

      const parentMenuItems = this.menuItemService.getItemsById(this.menuItems, [mutation.parentId]);
      if (parentMenuItems.length > 0) {
        const parent = parentMenuItems[0];
        const newItem = this.menuHierarchyService.mapPayloadToMenuItem(
          mutation.id,
          mutation.name,
          mutation.twinType,
          mutation.baseType,
          parent,
          this.effectiveFlowType,
        );

        if (!parent.children) {
          parent.children = [];
        }
        parent.children.push(newItem);
        parent.collapsed = false;

        // Ensure Angular detects the change in the array references
        this.menuItems = [...this.menuItems];
        if (this.filteredMenuItems) {
          // Find parent in filteredMenuItems and add it there as well, or just reset the filter if there's no active search
          if (!this.searchInput$.value || this.searchInput$.value.length < 3) {
            this.filteredMenuItems = this.menuItems;
          } else {
            const parentInFiltered = this.menuItemService.getItemsById(this.filteredMenuItems, [mutation.parentId]);
            if (parentInFiltered.length > 0) {
              if (!parentInFiltered[0].children) {
                parentInFiltered[0].children = [];
              }
              parentInFiltered[0].children.push(newItem);
              parentInFiltered[0].collapsed = false;
              this.filteredMenuItems = [...this.filteredMenuItems];
            }
          }
        }
      }
    }
  }

  private addSelectableClass(): void {
    this.dropTargetIds.forEach((id) => {
      const li = this.document.getElementById(id);
      if (li && li.tagName.toLowerCase() === 'li') {
        li.classList.add('selectable');
      }
    });
  }

  private removeSelectableClass(): void {
    this.document.querySelectorAll('li.selectable')?.forEach((x) => x.classList.remove('selectable'));
  }

  private removeSelectedClass(): void {
    this.document.querySelectorAll('li.selected').forEach((element) => element.classList.remove('selected'));
    this.document
      .querySelectorAll('li.not-allowed-target')
      .forEach((element) => element.classList.remove('not-allowed-target'));
    this.document.querySelector('nav + ul.not-allowed')?.classList.remove('not-allowed');
  }

  private updateChangeDetection(): void {
    this.hasChanged =
      JSON.stringify([...this.initialSelection].sort()) !== JSON.stringify([...this.selectedItems].sort());
  }

  private mapOrganizationToMenuItems(organization: IOrganizationResponse): IMenuItem[] {
    return this.menuHierarchyService.mapOrganizationToMenuItems(organization, this.effectiveFlowType);
  }

  private filterMenuItemsByDashboardType(menuItems: IMenuItem[]): IMenuItem[] {
    return this.menuHierarchyService.filterMenuItemsByDashboardType(menuItems, this.dashboardType);
  }

  private generateBreadCrumbs(): void {
    const items = this.menuItemService.getItemsById(this.menuItems ?? [], this.initialSelection);

    if (!items || items.length > 1) {
      this.breadcrumbsService.setBreadCrumbs(null);
      return;
    }

    const breadcrumbs = [];
    let currentItem: IMenuItem | undefined = items[0];
    if (!currentItem && this.menuItems) {
      // Add root node when nothing is selected
      breadcrumbs.push({ name: this.menuItems[0].name } as IBreadcrumb);
    }

    while (currentItem) {
      breadcrumbs.unshift({
        name: currentItem.name,
        route: this.pageRoute,
        queryParams: this.generateQueryParamsForMenuItem(currentItem.queryParams ?? {}),
      } as IBreadcrumb);
      currentItem = currentItem.parent;
    }

    this.breadcrumbsService.setBreadCrumbs(breadcrumbs);
  }

  /**
   * Sync collapse state from source items to target items
   */
  private syncCollapseState(sourceItems: IMenuItem[], targetItems: IMenuItem[]): void {
    const collapseMap = new Map<string, boolean>();

    // Build a map of collapse states from source items
    const buildCollapseMap = (items: IMenuItem[]): void => {
      items.forEach((item) => {
        collapseMap.set(item.id, item.collapsed);
        if (item.children) {
          buildCollapseMap(item.children);
        }
      });
    };

    // Apply collapse states to target items
    const applyCollapseState = (items: IMenuItem[]): void => {
      items.forEach((item) => {
        const collapseState = collapseMap.get(item.id);
        if (collapseState !== undefined) {
          item.collapsed = collapseState;
        }
        if (item.children) {
          applyCollapseState(item.children);
        }
      });
    };

    buildCollapseMap(sourceItems);
    applyCollapseState(targetItems);
  }

  /**
   * Expand menu items to show currently selected items (based on selectedItems array)
   */
  private expandToShowSelectedItems(): void {
    if (!this.menuItems) {
      return;
    }

    // First, collapse all items that are not disabled from collapsing
    const collapseAllItems = (items: IMenuItem[]): void => {
      items.forEach((item) => {
        if (!item.collapseDisabled) {
          item.collapsed = true;
        }
        if (item.children) {
          collapseAllItems(item.children);
        }
      });
    };

    // If no items are selected, just collapse everything and return
    if (!this.selectedItems.length) {
      collapseAllItems(this.menuItems);
      return;
    }

    // Start by collapsing everything
    collapseAllItems(this.menuItems);

    this.expandParentsOfSelected(this.menuItems);
  }

  private expandParentsOfSelected(items: IMenuItem[]): boolean {
    let hasSelectedChild = false;

    for (const item of items) {
      let shouldExpand = false;

      // Check if this item is selected
      if (this.selectedItems.includes(item.id)) {
        hasSelectedChild = true;
      }

      // Check children recursively
      if (item.children) {
        const childHasSelected = this.expandParentsOfSelected(item.children);
        if (childHasSelected) {
          shouldExpand = true;
          hasSelectedChild = true;
        }
      }

      // Expand this item if it contains selected items (but is not selected itself)
      if (shouldExpand) {
        item.collapsed = false;
      }
    }

    return hasSelectedChild;
  }

  /**
   * Expands the hierarchy to reveal all assets by expanding all parent nodes.
   * Continues expanding until asset nodes are reached.
   * @param items The menu items to search through
   */
  private expandToFirstAsset(items: IMenuItem[]): void {
    for (const item of items) {
      // If this item is not an asset and has children, expand it
      if (item.baseType !== BaseType.Asset && item.children && item.children.length > 0) {
        item.collapsed = false;
        this.expandToFirstAsset(item.children);
      }
    }
  }

  private filterTombstonesFromMenuItems(items: IMenuItem[]): IMenuItem[] {
    return items
      .filter((item) => !this.mutationSyncService.isDeleted(item.id))
      .map((item) => {
        if (item.children) {
          item.children = this.filterTombstonesFromMenuItems(item.children);
        }
        return item;
      });
  }

  private loadHierarchy(): void {
    if (!this.hasFlowChanged) {
      return;
    }

    this.hasFlowChanged = false;

    const flow = this.effectiveFlowType ?? OrganizationHierarchyFlowType.Space;

    this.spatialService
      .getOrganizationHierarchy([], [], undefined, flow, undefined, this.dashboardType)
      .pipe(
        map((organization) => this.mapOrganizationToMenuItems(organization)),
        map((menuItems) => this.filterMenuItemsByDashboardType(menuItems)),
        map((menuItems) => this.filterTombstonesFromMenuItems(menuItems)),
        tap((menuItems) => {
          this.menuItems = menuItems;
          this.filteredMenuItems = menuItems;

          // Apply base type filtering if multi-select is always enabled
          if (this.isMultiSelectAlwaysEnabled && this.allowedBaseTypes) {
            this.menuItemService.disableBasedOnType(this.menuItems, undefined, this.allowedBaseTypes);
          }

          const selectedItems = this.menuItemService.getItemsById(this.menuItems, this.selectedItems ?? []);

          // Reset the selection when the switched-to page's hierarchy tree does not contain the selected asset.
          if (selectedItems.length !== this.selectedItems.length) {
            this.selectedItems = [];
            this.initialSelection = [];
            this.queryParametersService.setObjectIds();
          }

          selectedItems.forEach((item) => {
            item.selected = true;
            this.onSelectionChange(item);
          });

          // Apply auto-expansion strategy if configured and no items are selected
          if (selectedItems.length === 0 && this.autoExpandStrategy === 'firstAsset') {
            this.expandToFirstAsset(this.menuItems);
          } else {
            this.expandToShowSelectedItems();
          }

          this.generateBreadCrumbs();

          // Only auto-manage comparison mode if multi-select is not always enabled
          if (!this.isMultiSelectAlwaysEnabled) {
            if (selectedItems.length >= 2) {
              this.isComparisonEnabled = true;
            } else {
              this.isComparisonEnabled = false;
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private updateDropTargetIds(): void {
    const menuItem = this.grabbedMenuItem;
    if (!menuItem) return;

    const menuItemType = menuItem.type;
    const allowed = this.allowedParentTypes?.get(menuItemType) ?? [];
    const dropTargetsById = new Map<string, IMenuItem>();

    let dropTargetIds = this.getParentNodeIds(this.filteredMenuItems, allowed, dropTargetsById);

    if (this.shouldRestrictToSameBuilding(menuItem)) {
      const sourceBuildingId = this.getAncestorBuildingId(menuItem);

      if (!sourceBuildingId) {
        dropTargetIds = [];
      } else {
        dropTargetIds = dropTargetIds.filter((id) => {
          const dropTarget = dropTargetsById.get(id);
          return dropTarget ? this.getAncestorBuildingId(dropTarget) === sourceBuildingId : false;
        });
      }
    }

    this.dropTargetIds = dropTargetIds;
    setTimeout(() => this.addSelectableClass(), 0);
  }

  private getParentNodeIds(
    items: IMenuItem[] | undefined,
    allowedTypes: string[],
    dropTargetsById: Map<string, IMenuItem>,
  ): string[] {
    if (!items) return [];
    let ids: string[] = [];
    for (const item of items) {
      if (allowedTypes.includes(item.type)) {
        ids.push(item.id);
        dropTargetsById.set(item.id, item);
      }
      if (!item.collapsed && item.children && item.children.length > 0) {
        ids = ids.concat(this.getParentNodeIds(item.children, allowedTypes, dropTargetsById));
      }
    }
    return ids;
  }

  private expandHoveredMenuItem(element: Element | null): void {
    if (!element) return;

    const hovered = this.findMenuItemFromElement(element);
    if (hovered && hovered.children && hovered.children.length > 0 && hovered.collapsed) {
      hovered.collapsed = false;
      this.updateDropTargetIds();
    }
  }

  private shouldRestrictToSameBuilding(item: IMenuItem): boolean {
    return this.effectiveFlowType === OrganizationHierarchyFlowType.Meter && item.baseType === BaseType.Asset;
  }

  private getAncestorBuildingId(item: IMenuItem): string | undefined {
    let current: IMenuItem | undefined = item;

    while (current) {
      if (current.twinType === TwinTypes.Building) {
        return current.id;
      }

      current = current.parent;
    }

    return undefined;
  }

  private isSameBuildingMove(source: IMenuItem, target: IMenuItem): boolean {
    const sourceBuildingId = this.getAncestorBuildingId(source);
    const targetBuildingId = this.getAncestorBuildingId(target);

    return sourceBuildingId !== undefined && sourceBuildingId === targetBuildingId;
  }

  private findMenuItemById(items: IMenuItem[] | undefined, targetId: string): IMenuItem | undefined {
    if (!items) return undefined;

    for (const item of items) {
      if (item.id === targetId) {
        return item;
      }

      if (item.children) {
        const found = this.findMenuItemById(item.children, targetId);
        if (found) {
          return found;
        }
      }
    }

    return undefined;
  }

  private findMenuItemFromElement(element: Element): IMenuItem | undefined {
    // Walk up the DOM to find the element that has an id
    let node: Element | null = element;
    while (node && !node.id) {
      node = node.parentElement;
    }

    const id = node?.id;
    if (!id || !this.filteredMenuItems) return;

    return this.findMenuItemById(this.filteredMenuItems, id);
  }

  private applyFlowState(queryFlowType?: OrganizationHierarchyFlowType): void {
    const previousFlowType = this.effectiveFlowType;
    const storedPreference = this.currentUserId
      ? this.hierarchyFlowPreferenceService.getPreferredFlow(this.currentUserId)
      : undefined;
    const preferredFlowType =
      queryFlowType ??
      (this.hasExplicitFlowPreference ? this.preferredFlowType : undefined) ??
      storedPreference ??
      OrganizationHierarchyFlowType.Space;

    this.preferredFlowType = preferredFlowType;
    this.effectiveFlowType = this.canFlow ? this.preferredFlowType : OrganizationHierarchyFlowType.Space;
    this.isEnergyFlowActive = this.canFlow && this.effectiveFlowType === OrganizationHierarchyFlowType.Meter;
    this.hierarchyFlowType$.next(this.effectiveFlowType);

    if (previousFlowType !== this.effectiveFlowType) {
      this.hasFlowChanged = true;
    }
  }
}

export interface IHierarchyMenuConfig {
  hierarchyMenu?: {
    type: DashboardType;
    edit?: true;
    select?: true;
    flow?: true;
    autoExpand?: 'firstAsset' | 'none';
    allowedBaseTypes?: BaseType[];
    isMultiSelectEnabled?: true;
  };
}
